import { Pagination } from "@/common/components/Table/pagination";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { MetaResponse } from "@/common/types/metaResponse";
import { cn } from "@/common/utils/cn";
import BranchRow from "./BranchRow";
import { ParsedBranchesEquipmentsConsumptionsTotalizerData } from "@/features/movementsTotalizer/types/branchesEquipmentsConsumptionsTotalizer.type";
import { branchesEquipmentsConsumptionsTotalizerColumns } from "@/features/movementsTotalizer/constants/branchesEquipmentsConsumptionsTotalizerColumns.constant";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";

interface BranchesEquipmentsConsumptionsTotalizerTableProps {
  className?: string;
  print?: boolean;
  data: ParsedBranchesEquipmentsConsumptionsTotalizerData[] | undefined;
  meta: MetaResponse | undefined;
  fetchAllBranchEquipments?: (params: {
    clientBranchId: string;
    equipmentsCountLimit: number;
  }) => void;
}

const BranchesEquipmentsConsumptionsTotalizerTable = ({
  className,
  print,
  data,
  meta,
  fetchAllBranchEquipments,
}: BranchesEquipmentsConsumptionsTotalizerTableProps) => {
  const {
    filterParams,
    setFilterParams,
  } = useFilterParams();

  return (
    <div className={cn(
      `flex-1 flex flex-col overflow-hidden rounded-lg
      print:text-black print:bg-white-800 print:rounded print:overflow-visible`,
      className,
    )}>
      {Number(data?.length) === 0 && !print && (
        <EmptyDataMessage className="flex-1" />
      )}

      {Number(data?.length) === 0 && print && (
        <div className="flex-1">
          <table className="text-base w-full table-fixed">
            <tbody>
              <tr>
                <td className="text-center py-8">
                  Não há registros para serem visualizados
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {Number(data?.length) !== 0 && (
        <div className="flex-1 overflow-auto thin-scroll rounded-lg">
          <table className={cn(
            'text-sm w-full',
            !print && 'whitespace-nowrap'
          )}>
            <colgroup>
              {branchesEquipmentsConsumptionsTotalizerColumns.map((col) => (
                <col key={col.value} className={cn(!print && 'min-w-40')} />
              ))}
            </colgroup>

            <thead className="sticky top-0 z-10 rounded-l-lg">
              <tr
                className={`
              bg-[#212023] [&>*:first-child]:border-l-0 [&>*:last-child]:border-r-0 rounded-l-lg
              print:bg-neutral-400/65 print:border-x print:border-x-neutral-400/65
              `}
              >
                {branchesEquipmentsConsumptionsTotalizerColumns.map((column) => (
                  <th
                    key={column.value}
                    className={`
                    text-left px-4 py-6 border-x border-x-[#64676980] text-sm font-bold first:rounded-l-lg
                    print:'px-2 print:py-2 print:first:rounded-l-none print:border-neutral-500',
                  `}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>

            {data?.map((branch) => (
              <BranchRow
                key={branch.id}
                branch={branch}
                fetchAllBranchEquipments={fetchAllBranchEquipments}
                print={print}
              />
            ))}

            {print && (
              <tbody>
                <tr>
                  <td colSpan={branchesEquipmentsConsumptionsTotalizerColumns.length} className="p-0 border-x border-neutral-400/50">
                    <div className="flex items-center justify-between gap-8 p-3 rounded-b-none bg-neutral-400/50 print:font-semibold">
                      <p className="">
                        Valor total das filiais
                      </p>

                      <p>
                        {data?.[0]?.branchesTotalAmount || '--'}
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      )}

      {!print && (
        <div className="w-full bg-black-300 rounded-lg flex justify-end items-center p-3 print:hidden">
          <Pagination
            currentPage={Number(filterParams.page) || 1}
            setPage={page => setFilterParams(prev => ({ ...prev, page }))}
            totalPages={meta?.total_pages || 1}
          />
        </div>
      )}
    </div>
  );
}

export default BranchesEquipmentsConsumptionsTotalizerTable;
